"use client";

import { useSession } from "@/components/SupabaseProvider";
import dayjs from "dayjs";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { ComponentPropsWithoutRef, FC, useEffect } from "react";
import {
  failedRenewalSentence,
  FailedPayment,
  findFailedPayment,
  findPayableSubscription,
  hasPaymentDue,
  SubscriptionsResponse,
} from "../helpers/failedPaymentWall";
import { pastDueAccessEndsAt } from "../helpers/pastDueSubscription";
import { useIsEmbedded } from "../hooks/useIsEmbedded";
import { useProfile } from "../hooks/useProfile";
import { useSubscriptions } from "../hooks/useSubscriptions";
import { useSubscriptionStatusUpdate } from "../hooks/useSubscriptionStatusUpdate";
import { useUpdatePayment } from "../hooks/useUpdatePayment";
import Button from "./Button";
import Typography from "./Typography";

// "Showed" fires once per page load, shared by both copies: the app-level
// banner stays mounted across navigations, but `/schedule` and the editor
// mount their own copy on every entry, so a per-instance flag would count
// each visit.
const trackedShow = { current: false };

type SubscriptionIssueBannerProps = {
  /** Mounted by the `/schedule` workspace or the editor inside its own
   *  height. */
  inWorkspace?: boolean;
} & ComponentPropsWithoutRef<"div">;

const SubscriptionIssueBanner: FC<SubscriptionIssueBannerProps> = ({
  inWorkspace = false,
  ...rest
}) => {
  const posthog = usePostHog();
  const session = useSession();
  const { data: profile } = useProfile();
  const { data: subscriptions } = useSubscriptions();
  const { mutate: updateSubscriptionStatus, isPending: isUpdating } =
    useSubscriptionStatusUpdate();
  const { updatePayment, isPending: isOpeningPayment } = useUpdatePayment();
  const isEmbedded = useIsEmbedded();
  const pathname = usePathname();

  // Paused comes from the profile. Past due comes from Stripe: profiles
  // never store it and past-due users keep full access while the renewal is
  // retried, so this banner is their only warning (see
  // helpers/pastDueSubscription.ts). Only while there's something to pay
  // (hasPaymentDue): an old past-due plan a newer active plan replaced owes
  // nothing, and a new card would pay its invoice a second time.
  const response = subscriptions as SubscriptionsResponse | undefined;
  const list = subscriptions?.subscriptions as any[] | undefined;
  const pastDueSubscription = findPayableSubscription(response);
  const subscriptionStatus =
    profile?.subscriptionStatus === "paused"
      ? "paused"
      : hasPaymentDue(response) || profile?.subscriptionStatus === "past_due"
        ? "past_due"
        : profile?.subscriptionStatus;
  const accessEndsAt =
    pastDueSubscription?.status === "past_due"
      ? pastDueAccessEndsAt(pastDueSubscription)
      : null;
  const accessEndsAtMs = accessEndsAt?.getTime() ?? null;
  // The failed-payment wall's test arm, from day 3 (helpers/failedPaymentWall.ts):
  // the banner says what's paused and opens the same payment page as the
  // wall. Control and days 0-2 keep the banner below as it was.
  const walled =
    subscriptionStatus === "past_due" && !!response?.failed_payment_wall?.blocked;
  const failedPayment = walled ? findFailedPayment(response) : undefined;
  // `/schedule` and the editor mount their own copy inside their
  // full-height layouts (see FreeTrialBanner and Editor.tsx).
  const routeMountsOwnCopy =
    pathname === "/schedule" || !!pathname?.startsWith("/editor/");
  const shouldShow =
    !!session &&
    !isEmbedded &&
    (!routeMountsOwnCopy || inWorkspace) &&
    (subscriptionStatus === "past_due" || subscriptionStatus === "paused");

  useEffect(() => {
    if (!shouldShow || trackedShow.current) return;
    trackedShow.current = true;
    posthog?.capture("Subscription Issue Banner Showed", {
      subscription_status: subscriptionStatus,
      access_ends_at: accessEndsAtMs
        ? new Date(accessEndsAtMs).toISOString()
        : null,
      failed_payment_wall_blocked: walled,
    });
  }, [shouldShow, subscriptionStatus, accessEndsAtMs, walled, posthog]);

  if (!shouldShow) return <></>;

  const accessEndsLabel = accessEndsAt
    ? dayjs(accessEndsAt).format("MMMM D")
    : null;

  // Try to pick the first subscription id we can act upon
  let actionableSubscriptionId: string | undefined = undefined;
  if (list && list.length > 0) {
    if (subscriptionStatus === "paused") {
      const paused = list.find((s) => s.pause_collection);
      actionableSubscriptionId = paused ? paused.id : list[0].id;
    } else {
      actionableSubscriptionId = list[0].id;
    }
  }

  const handleManage = async () => {
    try {
      posthog?.capture("Manage Billing Clicked (status banner)", {
        source: "subscription-status-banner",
        subscription_status: subscriptionStatus,
      });
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/billing/subscription`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      ).then((r) => r.json());
      if (res.portal_url) window.location.assign(res.portal_url);
    } catch (e) {
      // no-op; errors are surfaced in the billing page already
    }
  };

  const handleResume = () => {
    if (!actionableSubscriptionId) return;
    posthog?.capture("Resume Subscription Clicked (status banner)", {
      source: "subscription-status-banner",
    });
    updateSubscriptionStatus({
      subscriptionId: actionableSubscriptionId,
      action: "resume",
    });
  };

  return (
    <SubscriptionIssueBannerView
      {...rest}
      subscriptionStatus={subscriptionStatus as "past_due" | "paused"}
      walled={walled}
      failedPayment={failedPayment}
      accessEndsLabel={accessEndsLabel}
      onUpdatePayment={
        // Nothing to pay after all ("nothing_due"): the hook's fresh list
        // hides the banner.
        walled
          ? () =>
              updatePayment("banner", {
                days_past_due:
                  response?.failed_payment_wall?.days_past_due ?? null,
              })
          : handleManage
      }
      updatingPayment={walled && isOpeningPayment}
      onResume={handleResume}
      resuming={isUpdating}
      canResume={!!actionableSubscriptionId}
    />
  );
};

type SubscriptionIssueBannerViewProps = {
  subscriptionStatus: "past_due" | "paused";
  /** The failed-payment wall's test arm, day 3+. */
  walled?: boolean;
  failedPayment?: FailedPayment;
  accessEndsLabel?: string | null;
  onUpdatePayment?: () => void;
  updatingPayment?: boolean;
  onResume?: () => void;
  resuming?: boolean;
  canResume?: boolean;
} & ComponentPropsWithoutRef<"div">;

// The banner itself, from props (also for /dev/failed-payment-wall).
export const SubscriptionIssueBannerView: FC<SubscriptionIssueBannerViewProps> = ({
  subscriptionStatus,
  walled = false,
  failedPayment,
  accessEndsLabel,
  onUpdatePayment,
  updatingPayment,
  onResume,
  resuming,
  canResume,
  ...rest
}) => {
  return (
    <div
      {...rest}
      className={`${rest.className} flex justify-center items-center gap-3 dark px-3 py-1 bg-slate-950 text-white border-b border-slate-800 z-0`}
    >
      <div className=" items-baseline md:gap-2">
        {subscriptionStatus === "past_due" && walled && (
          <Typography variant="caption" className="text-white block">
            {failedRenewalSentence(failedPayment)} Generating, exporting and
            publishing are paused until you update your card.
          </Typography>
        )}
        {subscriptionStatus === "past_due" && !walled && (
          <>
            <Typography variant="caption" className="text-white block">
              Your last payment failed. Update your payment method to keep
              your plan
              {accessEndsLabel
                ? `, or you'll lose access on ${accessEndsLabel}`
                : ""}
              .
            </Typography>
          </>
        )}
        {subscriptionStatus === "paused" && (
          <>
            <Typography variant="caption" className="text-white block">
              Your subscription is paused. Please resume your subscription to
              continue using all features.
            </Typography>
          </>
        )}
      </div>
      <div className="flex gap-1">
        {subscriptionStatus === "past_due" && (
          <Button
            size="m"
            variant="primary"
            onClick={onUpdatePayment}
            loading={updatingPayment}
          >
            Update payment
          </Button>
        )}
        {subscriptionStatus === "paused" && (
          <Button
            size="m"
            variant="primary"
            onClick={onResume}
            loading={resuming}
            disabled={!canResume}
          >
            Resume
          </Button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionIssueBanner;
